import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { CalendarIcon, ArrowRight, Tag } from "lucide-react";

const PICKUP_LOCATIONS = [
  "Easy Drive Office — West Bay Street",
  "Lynden Pindling Intl Airport (NAS)",
  "Downtown Nassau",
  "Cable Beach",
  "Paradise Island",
  "Prince George Wharf (Cruise Port)",
  "Hotel / Villa Delivery",
];
const DROPOFF_LOCATIONS = PICKUP_LOCATIONS;

function DateField({ label, date, setDate, minDate, testid, disabledRanges = [], blockSaturdayPickup = false }) {
  return (
    <div className="flex-1 min-w-[150px]">
      <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-1.5">{label}</p>
      <Popover>
        <PopoverTrigger asChild>
          <button
            data-testid={testid}
            className="w-full flex items-center gap-2 h-10 rounded-lg border border-input bg-white px-3 text-sm font-ui text-left hover:border-primary/50 transition-colors duration-200"
          >
            <CalendarIcon size={15} className="gold-text shrink-0" />
            <span className={date ? "" : "text-muted-foreground"}>{date ? format(date, "MMM d, yyyy") : "Pick a date"}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0 bg-white" align="start">
          <Calendar
            mode="single"
            selected={date}
            onSelect={setDate}
            disabled={[{ before: minDate || new Date() }, ...(blockSaturdayPickup ? [{ daysOfWeek: [6] }] : []), ...disabledRanges]}
            initialFocus
          />
          {blockSaturdayPickup && (
            <p data-testid={`${testid}-saturday-note`} className="px-4 pb-3 text-[11px] text-muted-foreground font-ui leading-snug max-w-[260px]">
              Closed Saturdays for pickups — Saturday returns are welcome.
            </p>
          )}
          {disabledRanges.length > 0 && (
            <div data-testid={`${testid}-blackout-hint`} className="px-4 pb-3 space-y-1 max-w-[260px]">
              <p className="text-[10px] font-ui tracking-[0.15em] uppercase text-muted-foreground">Unavailable (blackout):</p>
              {disabledRanges.map((r, i) => (
                <p key={i} data-testid={`${testid}-blackout-reason-${i}`} className="text-[11px] text-muted-foreground font-ui leading-snug">
                  {r.label}
                </p>
              ))}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}

function LocationField({ label, value, onChange, testid, locations }) {
  return (
    <div className="flex-1 min-w-[180px]">
      <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-1.5">{label}</p>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 rounded-lg border border-input bg-white px-3 text-sm font-ui hover:border-primary/50 transition-colors duration-200"
      >
        {locations.map((l) => (
          <option key={l} value={l}>{l}</option>
        ))}
      </select>
    </div>
  );
}

function TimeField({ label, value, onChange, testid }) {
  return (
    <div className="min-w-[110px]">
      <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-1.5">{label}</p>
      <input
        data-testid={testid}
        type="time"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 rounded-lg border border-input bg-white px-3 text-sm font-ui hover:border-primary/50 transition-colors duration-200"
      />
    </div>
  );
}

export default function BookingBar() {
  const navigate = useNavigate();
  const [pickupLocation, setPickupLocation] = useState(PICKUP_LOCATIONS[0]);
  const [dropoffLocation, setDropoffLocation] = useState(DROPOFF_LOCATIONS[0]);
  const [pickupDate, setPickupDate] = useState(null);
  const [dropoffDate, setDropoffDate] = useState(null);
  const [pickupTime, setPickupTime] = useState("10:00");
  const [dropoffTime, setDropoffTime] = useState("10:00");
  const [couponCode, setCouponCode] = useState("");
  const [blackouts, setBlackouts] = useState([]);
  const [onLotToday, setOnLotToday] = useState(null);

  useEffect(() => {
    const urlCoupon = new URLSearchParams(window.location.search).get("coupon");
    if (urlCoupon) setCouponCode(urlCoupon.trim().toUpperCase());
    api.get("/vehicles").then((r) => setOnLotToday(r.data.filter((v) => v.lot_status === "on_lot").length)).catch(() => {});
    api
      .get("/blackouts/public")
      .then((r) =>
        setBlackouts(
          r.data.map((x) => {
            const from = new Date(x.start + "T00:00:00");
            const to = new Date(new Date(x.end + "T00:00:00").getTime() - 86400000);
            return {
              from,
              to,
              label: `${format(from, "MMM d")} – ${format(to, "MMM d")}${x.reason ? ` — ${x.reason}` : ""}`,
            };
          })
        )
      )
      .catch(() => {});
  }, []);

  const submit = () => {
    const params = new URLSearchParams({
      pickup_location: pickupLocation,
      dropoff_location: dropoffLocation,
      pickup_time: pickupTime,
      dropoff_time: dropoffTime,
    });
    if (pickupDate) params.set("pickup_date", format(pickupDate, "yyyy-MM-dd"));
    if (dropoffDate) params.set("dropoff_date", format(dropoffDate, "yyyy-MM-dd"));
    if (couponCode.trim()) params.set("coupon", couponCode.trim().toUpperCase());
    navigate(`/fleet?${params.toString()}`);
  };

  return (
    <div data-testid="booking-bar" className="relative max-w-6xl mx-auto px-6">
      <div data-testid="booking-bar-price-ribbon" className="absolute -top-3.5 left-10 z-10 inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground text-[11px] font-ui font-semibold tracking-wide px-4 py-1.5 shadow-[0_4px_14px_rgba(199,30,46,0.4)]">
        <Tag size={11} /> From $55/day
      </div>
      <div className="glass border rounded-2xl shadow-xl shadow-black/5 p-3 md:p-4">
        <div className="flex flex-wrap items-end gap-3">
          <LocationField label="Pickup location" value={pickupLocation} onChange={setPickupLocation} testid="bar-pickup-location" locations={PICKUP_LOCATIONS} />
          <DateField label="Pickup date" date={pickupDate} setDate={setPickupDate} testid="bar-pickup-date" disabledRanges={blackouts} blockSaturdayPickup />
          <TimeField label="Pickup time" value={pickupTime} onChange={setPickupTime} testid="bar-pickup-time" />
          <LocationField label="Drop-off location" value={dropoffLocation} onChange={setDropoffLocation} testid="bar-dropoff-location" locations={DROPOFF_LOCATIONS} />
          <DateField label="Drop-off date" date={dropoffDate} setDate={setDropoffDate} minDate={pickupDate || new Date()} testid="bar-dropoff-date" disabledRanges={blackouts} />
          <TimeField label="Drop-off time" value={dropoffTime} onChange={setDropoffTime} testid="bar-dropoff-time" />
          <div className="w-[118px] shrink-0">
            <p className="font-ui text-[10px] tracking-[0.2em] uppercase text-muted-foreground mb-1.5">Discount code</p>
            <div className="relative">
              <Tag size={12} className="gold-text absolute left-2.5 top-1/2 -translate-y-1/2" />
              <input
                data-testid="bar-coupon-code"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                placeholder="CODE"
                maxLength={12}
                className="w-full h-9 rounded-lg border border-input bg-white pl-7 pr-2 text-xs font-ui uppercase hover:border-primary/50 transition-colors duration-200"
              />
            </div>
          </div>
          <Button data-testid="bar-find-vehicle-button" onClick={submit} className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90 font-ui h-10 px-5 text-sm">
            Find Your Vehicle <ArrowRight size={15} className="ml-2" />
          </Button>
        </div>
      </div>
      {onLotToday !== null && (
        <p data-testid="bar-urgency-line" className="mt-3 text-center text-xs font-ui tracking-[0.15em] uppercase text-muted-foreground">
          Only <span className="text-primary font-bold">{onLotToday}</span> {onLotToday === 1 ? "vehicle" : "vehicles"} on the lot today — book early
        </p>
      )}
    </div>
  );
}
